import React from "react";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { MediaStage, MediaStatus } from "@/core/types/mediaTypes";
import { AspectRatio } from "./MediaCardContainer";
import { MediaCard } from "./MediaCard";
import * as Badge from "./MediaBadges";
import { StageDropdown } from "@/core/MediaItem/StageDropdown";
import { MediaItemLabelModal } from "@/core/MediaItem/MediaItemLabelModal";
import { UploadRevisionModal } from "./UploadRevisionModal";
import { ContextMenu } from "./ContextMenu";
import { useAdminMediaActions } from "./hooks/useAdminMediaActions";

interface AdminMediaCardWrapperProps {
  mediaItem: MediaItem;
  clientId: string;
  aspectRatio?: AspectRatio;
  isSelected?: boolean;
  selectionIndex?: number;
  showNumbering?: boolean;
  onClick?: () => void;
  onStageChange?: (updatedItem: MediaItem, oldStage: MediaStage) => void;
  onMediaItemDeleted?: (id: number) => void;
  onEditClick?: (item: MediaItem) => void;
  onActionError?: (error: string) => void;
  onAddToCollection?: (mediaItemId: number) => void;
  selectionMode?: {
    isActive: boolean;
    selectedIds: number[];
    onToggle: (id: number, event: React.MouseEvent) => void;
  };
  /** Extra classes on the card container (e.g. `border-0` inside panels). */
  className?: string;
}

/**
 * AdminMediaCard using unified MediaCard component
 *
 * This wrapper adds admin-specific behavior to the generic MediaCard:
 * - Stage management
 * - Context menus
 * - Label editing
 * - Batch polling
 * - Selection mode
 */
export const AdminMediaCardWrapper: React.FC<AdminMediaCardWrapperProps> = ({
  mediaItem,
  clientId,
  aspectRatio = "4:5",
  isSelected = false,
  selectionIndex = 0,
  showNumbering = false,
  onClick,
  onStageChange,
  onMediaItemDeleted,
  onEditClick,
  onActionError,
  onAddToCollection,
  selectionMode,
  className,
}) => {
  // Admin actions hook
  const {
    isLabelModalOpen,
    downloadStatus,
    isRevisionModalOpen,
    handleStageChange,
    handleLabelClick,
    handleLabelUpdate,
    setIsLabelModalOpen,
    setIsRevisionModalOpen,
    contextMenuItems,
  } = useAdminMediaActions({
    mediaItem,
    clientId,
    onStageChange,
    onMediaItemDeleted,
    onEditClick,
    onActionError,
    onAddToCollection,
  });

  const isInSelectionMode = selectionMode?.isActive || false;
  const isItemSelected = selectionMode?.selectedIds.includes(mediaItem.id) || false;

  // Top-left: Selection checkbox or Stage dropdown
  const topLeft = isInSelectionMode ? (
    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
      isItemSelected
        ? 'bg-blue-500 border-blue-500'
        : 'bg-white/90 border-white/90'
    }`}>
      {isItemSelected && (
        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      )}
    </div>
  ) : (
    <>
      <StageDropdown
        mediaItem={mediaItem}
        onStageChange={handleStageChange}
        isChanging={false}
        disabled={false}
      />
      {mediaItem.isProcessingFailed() && (
        <Badge.ProcessingBadge status="failed" />
      )}
    </>
  );

  // Top-right: Context menu
  const topRight = !isInSelectionMode ? <ContextMenu items={contextMenuItems} /> : null;

  // Bottom-left: Selection, label, and comment badges
  const handleCommentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.();
  };

  const bottomLeft = (
    <>
      {isSelected && (
        <Badge.SelectionBadge
          index={selectionIndex}
          showNumber={showNumbering}
        />
      )}
      <Badge.LabelBadge
        label={mediaItem.label}
        onClick={handleLabelClick}
        showOnHover={!mediaItem.label}
      />
      {mediaItem.isImage() && (
        <Badge.CommentBadge
          comment={mediaItem.clientComment}
          onClick={handleCommentClick}
          showOnHover={!mediaItem.clientComment}
        />
      )}
    </>
  );

  // Bottom-right: Multiple badges (priority based). The public-status pill is
  // always-on so it stays visible without hover, mirroring the published
  // indicator on PostCard.
  const bottomRight = (
    <>
      {mediaItem.publicStatus === "public" && (
        <Badge.PublicBadge />
      )}

      {mediaItem.usageCount > 0 && (
        <Badge.UsageBadge count={mediaItem.usageCount} />
      )}

      {mediaItem.usageCount === 0 && downloadStatus && (
        <Badge.DownloadStatusBadge status={downloadStatus} />
      )}

      {mediaItem.usageCount === 0 && !downloadStatus && mediaItem.backendData.status === MediaStatus.PROCESSING_AI && (
        <Badge.AnalyzingBadge />
      )}

      {mediaItem.usageCount === 0 && !downloadStatus && mediaItem.backendData.status !== MediaStatus.PROCESSING_AI && mediaItem.frontendData.aiAnalysis?.instagramAppealScore && (
        <Badge.AiAnalysis mediaItem={mediaItem} anchorPosition="right" />
      )}

      {mediaItem.usageCount === 0 && !downloadStatus && mediaItem.backendData.status !== MediaStatus.PROCESSING_AI && mediaItem.group && !mediaItem.isTaken && (
        <Badge.GroupBadge group={mediaItem.group} />
      )}

      {/* Client rating badge - always rightmost */}
      {mediaItem.clientRating != null && (
        <Badge.RatingBadge rating={mediaItem.clientRating} />
      )}
    </>
  );

  return (
    <>
      <MediaCard
        mediaItem={mediaItem}
        viewMode="grid"
        aspectRatio={aspectRatio}
        isSelected={isSelected || isItemSelected}
        onSelect={isInSelectionMode && selectionMode ? selectionMode.onToggle : undefined}
        onClick={onClick}
        topLeft={topLeft}
        topRight={topRight}
        bottomLeft={bottomLeft}
        bottomRight={bottomRight}
        className={[isInSelectionMode ? 'cursor-pointer' : '', className ?? ''].join(' ').trim()}
      />

      <MediaItemLabelModal
        isOpen={isLabelModalOpen}
        onClose={() => setIsLabelModalOpen(false)}
        mediaItem={mediaItem}
        clientId={clientId}
        onLabelUpdate={handleLabelUpdate}
      />

      <UploadRevisionModal
        isOpen={isRevisionModalOpen}
        onClose={() => setIsRevisionModalOpen(false)}
        mediaItem={mediaItem}
        clientId={clientId}
        onUploadComplete={(updatedItem) => {
          onActionError?.("Revision uploaded. Processing will begin shortly.");
          onStageChange?.(updatedItem, mediaItem.stage as MediaStage);
        }}
        onError={(error) => onActionError?.(error)}
      />
    </>
  );
};

// Export with same name as original for drop-in replacement
export const AdminMediaCard = React.memo(AdminMediaCardWrapper);
